import React, { KeyboardEventHandler, useEffect, useState, useCallback } from 'react'
import CreatableSelect from 'react-select/creatable'
import { useTheme } from 'styled-components'
import { themeColors, styles } from './InputItems.theme'
import { Size } from './InputItems.types'

const components = {
  DropdownIndicator: null,
}

interface Option {
  readonly label: string
  readonly value: string
}

const createOption = (label: string) => ({
  label,
  value: label,
})

export interface Props {
  /** Callback for when values are changed */
  valuesChanged: (values: string[]) => void
  /** Initial items to be shown, changing won't have any effect after first render */
  initialValues?: string[]
  /** Whether to show X icon to clear all values */
  isClearable?: boolean
  /** Placeholder text */
  placeholder?: string
  /** Is input disabled */
  isDisabled?: boolean
  /** Has input any error */
  isError?: boolean
  /** Size of the input element */
  size: Size
}

const InputItems: React.FC<Props> = ({ valuesChanged, initialValues, isClearable, placeholder, isDisabled, isError, size }) => {
  const [inputValue, setInputValue] = useState('')
  const [values, setValues] = useState<readonly Option[]>(initialValues?.map(v => createOption(v)) || [])
  const [isDuplicateValue, setIsDuplicateValue] = useState(false)
  const appTheme = useTheme()

  const handleKeyDown: KeyboardEventHandler = useCallback(
    event => {
      if (!inputValue) return

      switch (event.key) {
        case 'Enter':
        case 'Tab':
          setValues(prev => {
            if (prev.some(o => o.value === inputValue)) {
              return prev
            }

            return [...prev, createOption(inputValue)]
          })
          setInputValue('')
          event.preventDefault()
          break
        default:
      }
    },
    [inputValue],
  )

  useEffect(() => {
    if (values.findIndex(o => o.value === inputValue) === -1) {
      setIsDuplicateValue(false)
    } else {
      setIsDuplicateValue(true)
    }
  }, [values, inputValue])

  useEffect(() => {
    valuesChanged(values.map(v => v.value))
  }, [values])

  return (
    <CreatableSelect
      isMulti
      components={components}
      inputValue={inputValue}
      isClearable={isClearable}
      isDisabled={isDisabled}
      menuIsOpen={false}
      onChange={newValue => setValues(newValue)}
      onInputChange={newValue => setInputValue(newValue)}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      value={values}
      styles={styles(appTheme, { isError, size, isDuplicateValue })}
      theme={theme => ({
        ...theme,
        colors: {
          ...theme.colors,
          ...themeColors(appTheme),
        },
      })}
    />
  )
}

InputItems.displayName = 'InputItems'
export default InputItems
